import { Box, withStyles, usePagination, BodyText, MaterialSymbol } from 'vopay';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SubAccountList, buildNestedSubAccounts } from 'shared/subAccountList';
import { usePermissions } from '@/providers/Permissions.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import { useToast } from '@/providers/Toast.provider';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import { Title, Button, UtilButton } from '@/components/UI';
import OrgChartContainer from './OrgChartContainer/OrgChartContainer';
import UserManagementDrawer from './UserManagement/UserManagementDrawer';
import WorkflowDrawer from './UserManagement/WorkflowDrawer';
import useSubAccountListHostProps from './useSubAccountListHostProps';
import styles from './SubAccount.module.scss';

const classes = withStyles(styles);

// Host chrome for the /sub-accounts-list route (ID-13193). The filters + table + paginator + row actions now
// live in the shared SubAccountList feature; the host owns the page header (view toggle, Block / Unblock
// Sub-Accounts, Add Sub-Account), the blocked banner, the org-chart view with its own fetch, and the User
// Management / Approval Workflow drawers (they wrap whole host features, so shared only asks for them).
export default function SubAccountsPage() {
  const history = useHistory();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List' });

  const { permissions } = usePermissions();
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();

  const {
    response: { AccountID, ShowSubAccountOrgChart, AccountType },
  } = useClientLogin();

  const [showOrgView, setShowOrgView] = useState(false);
  const [selectedAccountID, setSelectedAccountID] = useState<string>();
  const [selectedAccountName, setSelectedAccountName] = useState<string>();
  const [selectedAccountToken, setSelectedAccountToken] = useState<string>();
  const [isUserManagementDrawerOpen, setIsUserManagementDrawerOpen] = useState(false);
  const [isWorkflowDrawerOpen, setIsWorkflowDrawerOpen] = useState(false);

  const onOpenUserManagement = useCallback(({ accountName, token }: { accountName: string; token: string }) => {
    setSelectedAccountName(accountName);
    setSelectedAccountToken(token);
    setIsUserManagementDrawerOpen(true);
  }, []);

  const onOpenApprovalWorkflow = useCallback(
    ({ accountID, accountName, token }: { accountID: string; accountName: string; token: string }) => {
      setSelectedAccountName(accountName);
      setSelectedAccountToken(token);
      setSelectedAccountID(accountID);
      setIsWorkflowDrawerOpen(true);
    },
    [],
  );

  const hostProps = useSubAccountListHostProps({ onOpenUserManagement, onOpenApprovalWorkflow });

  const [orgChartResponse, , , fetchOrgChartSubAccounts] = useFetchSubAccounts({});

  const {
    pageIndex: orgChartPageIndex,
    setPageIndex: setOrgChartPageIndex,
    offset: orgChartOffset,
  } = usePagination(orgChartResponse.PaginationMetaData?.TotalRecords || 0, { pageSize: 10 });

  useEffect(() => {
    if (!showOrgView) return;

    const fetchData = async () => {
      const resp = await fetchOrgChartSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
        Limit: 10,
        Offset: orgChartOffset,
      });

      if (!resp.Success) toast.neutral(resp.ErrorMessage);
    };

    fetchData();
  }, [showOrgView, orgChartOffset]);

  const orgChartNestedSubAccounts = useMemo(
    () => buildNestedSubAccounts(orgChartResponse.Subaccounts || []).nested,
    [orgChartResponse],
  );

  return (
    <SubAccountList {...hostProps}>
      {({ allAccountsBlocked, blockAccount, unblockAccount, blockAllAccounts, unblockAllAccounts }) => (
        <Box flx="row" jc="center" className={classes('subAccountsListContainer')}>
          <Box flx="col" gapSize="33px" boxWidth="100%">
            <Box jc="sb" width="100%">
              <Title>{t('title', { defaultValue: 'Sub-Account List' })}</Title>

              <Box gapSize="16px" ai="center">
                {showOrgView && ShowSubAccountOrgChart && (
                  <UtilButton
                    onClick={() => {
                      setShowOrgView(false);
                    }}
                    iconLeftProps={{ icon: 'table_rows', fillAmount: 1 }}
                  >
                    {t('switchViewButton', { defaultValue: 'Switch View' })}
                  </UtilButton>
                )}

                {!showOrgView && ShowSubAccountOrgChart && (
                  <UtilButton
                    iconLeft="lan"
                    onClick={() => {
                      setShowOrgView(true);
                    }}
                  >
                    {t('switchViewButton', { defaultValue: 'Switch View' })}
                  </UtilButton>
                )}

                {AccountType === 'Secured Account' && permissions.ManageBlockedAccounts && (
                  <>
                    {!allAccountsBlocked && (
                      <Button colorScheme="secondary" onClick={() => blockAllAccounts()}>
                        {t('blockSubAccountsButton', { defaultValue: 'Block Sub-Accounts' })}
                      </Button>
                    )}

                    {allAccountsBlocked && (
                      <Button colorScheme="secondary" onClick={() => unblockAllAccounts()}>
                        {t('unblockSubAccountsButton', { defaultValue: 'Unblock Sub-Accounts' })}
                      </Button>
                    )}
                  </>
                )}

                {permissions.AddSubaccounts &&
                  (lockedFeatures.SubAccountPermissions ? (
                    <Button
                      id="add-sub-account"
                      colorScheme="primary"
                      onClick={() => {
                        history.push('/create-sub-account');
                      }}
                      iconLeft="add"
                    >
                      {t('createSubAccountButton', { defaultValue: 'Add Sub-Account' })}
                    </Button>
                  ) : (
                    <Button
                      id="add-sub-account"
                      onClick={() =>
                        updateIsLocked(true, t('createModuleName', { defaultValue: 'Create Sub-Account' }))
                      }
                      iconLeftProps={{ icon: 'lock' }}
                      colorScheme="primary"
                      className={styles.lockedButton}
                    >
                      {t('addSubAccountButton', { defaultValue: 'Add Sub-Account' })}
                    </Button>
                  ))}
              </Box>
            </Box>

            {AccountType === 'Secured Account' && allAccountsBlocked && (
              <div className={classes('infoBox')}>
                <div className={classes('infoBoxHeader')}>
                  <MaterialSymbol color="#2F82FF" size="20px" icon="block" />
                  <BodyText weight="em">{t('blockedInfoTitle', { defaultValue: 'Sub-Accounts are Blocked' })}</BodyText>
                </div>

                <BodyText className={classes('infoBoxDescription')}>
                  {t('blockedInfoDescription', {
                    defaultValue:
                      'Outbound transactions are disabled and Inbound transactions will deposit to the parent account.',
                  })}
                </BodyText>
              </div>
            )}

            {!showOrgView && <SubAccountList.Table />}

            {showOrgView && (
              <OrgChartContainer
                subAccounts={orgChartNestedSubAccounts}
                canCreateAccountTransfer={hostProps.userPermissions.canCreateAccountTransfer}
                accountBlocked={allAccountsBlocked}
                canManageBlocking={AccountType === 'Secured Account' && !!permissions.ManageBlockedAccounts}
                onBlockAccount={blockAccount}
                onUnblockAccount={unblockAccount}
                refresh={async () => {
                  const resp = await fetchOrgChartSubAccounts({
                    PortalUser: AccountID,
                    ShowToken: true,
                    Limit: 10,
                    Offset: orgChartOffset,
                  });

                  if (!resp.Success) toast.neutral(resp.ErrorMessage);
                }}
                hasRootNextServerPage={(orgChartResponse.PaginationMetaData?.TotalRecords || 0) > orgChartOffset + 10}
                hasRootPrevServerPage={orgChartOffset > 0}
                rootNextPage={() => setOrgChartPageIndex(orgChartPageIndex + 1)}
                rootPrevPage={() => setOrgChartPageIndex(Math.max(0, orgChartPageIndex - 1))}
                rootPageSize={10}
              />
            )}
          </Box>

          {selectedAccountToken && selectedAccountName && (
            <UserManagementDrawer
              accountToken={selectedAccountToken}
              accountName={selectedAccountName}
              open={isUserManagementDrawerOpen}
              onClose={() => setIsUserManagementDrawerOpen(false)}
            />
          )}

          {selectedAccountToken && selectedAccountName && (
            <WorkflowDrawer
              accountToken={selectedAccountToken}
              accountId={selectedAccountID}
              accountName={selectedAccountName}
              open={isWorkflowDrawerOpen}
              onClose={() => setIsWorkflowDrawerOpen(false)}
            />
          )}
        </Box>
      )}
    </SubAccountList>
  );
}
